import React, { useCallback, useEffect, useState } from 'react';
import '../styles/Home.css';

import PhoneFrame from './home/PhoneFrame';
import ArrowPill from './home/ArrowPill';
import StoreBadges from './home/StoreBadges';
import { useRevealOnScroll } from './home/useRevealOnScroll';
import { APP_STORE_URL, INSTAGRAM_URL, SUPPORT_EMAIL, trackStoreClick } from './home/links';

import wordmark from '../images/home/burst-wordmark-cream.png';
import gusPhoto from '../images/home/gus-running.webp';
import screenHomeDark from '../images/home/screens/home-dark.webp';
import screenTrainingWeek from '../images/home/screens/training-week.webp';
import screenCommunityRun from '../images/home/screens/community-run.webp';
import screenOnboardingGoal from '../images/home/screens/onboarding-goal.webp';
import screenProgrammeDetails from '../images/home/screens/programme-details.webp';
import screenMovementDeadlift from '../images/home/screens/movement-deadlift.webp';
import screenWorkoutGrid from '../images/home/screens/workout-grid.webp';
import screenEquipmentFilter from '../images/home/screens/equipment-filter.webp';
import screenMovementBench from '../images/home/screens/movement-bench.webp';
import screenHistory from '../images/home/screens/history.webp';
import screenLeaderboard from '../images/home/screens/leaderboard.webp';
import screenCommunityMuscle from '../images/home/screens/community-muscle.webp';

const NAV_LINKS = [
  { label: 'Programmes', id: 'programmes' },
  { label: 'Workouts', id: 'workouts' },
  { label: 'Coaching', id: 'coaching' },
  { label: 'Community', id: 'community' },
  { label: 'Pricing', id: 'pricing' },
];

const STEPS = [
  {
    number: '01',
    title: 'Pick a goal',
    body: 'Stronger, Bigger, a race distance, or both, and how many days a week you can give it.',
    screen: screenOnboardingGoal,
    alt: 'Choosing a training goal in the Burst app',
  },
  {
    number: '02',
    title: 'Set your time and kit',
    body: 'Session length, programme length, movement level and exactly what equipment you have, gym or home.',
    screen: screenProgrammeDetails,
    alt: 'Setting programme details in the Burst app',
  },
  {
    number: '03',
    title: "Train with a coach's eye",
    body: 'Every movement has a video, tempo, RPE and coaching cues. Log it in seconds and watch the history build.',
    screen: screenMovementDeadlift,
    alt: 'Movement video and coaching cues in the Burst app',
  },
];

const PROGRAMMES = [
  {
    stripe: 'running',
    label: 'Running · Race plans',
    title: '5K · 10K · Half\u00a0·\u00a0Marathon',
    body: 'Structured to your race date, with easy, tempo and interval sessions set to your pace.',
    facts: [
      ['5K and 10K', 'Up to 12 weeks'],
      ['Half marathon', 'Up to 16 weeks'],
      ['Marathon', 'Up to 23 weeks'],
    ],
  },
  {
    stripe: 'strength',
    label: 'Strength · KPI-driven',
    title: 'Stronger · Bigger',
    body: 'Choose Stronger or Bigger, then the key lifts you want to move. Every block is built around them, with tempo and RPE prescribed.',
    facts: [
      ['Length', '6 to 12 weeks'],
      ['KPIs', 'Up to 3 lifts, your choice'],
      ['Kit', 'Built to your equipment'],
    ],
  },
  {
    stripe: 'hybrid',
    label: 'Hybrid · Both, balanced',
    title: 'Faster\u00a0&\u00a0Stronger · Faster\u00a0&\u00a0Bigger',
    body: 'A race plan and a strength block scheduled together, so neither one eats the other.',
    facts: [
      ['Length', '6 to 12 weeks'],
      ['KPIs', 'A race time plus up to 3 lifts'],
      ['Kit', 'Built to your equipment'],
    ],
  },
];

const WORKOUT_TYPES = [
  { label: 'Strength', tone: 'strength' },
  { label: 'Running', tone: 'running' },
  { label: 'Erg', tone: 'erg' },
  { label: 'Hyrox', tone: 'hyrox' },
  { label: 'HIIT', tone: 'hiit' },
  { label: 'Mobility', tone: 'mobility' },
];

const WHY_ROWS = [
  ['Video every movement', 'Gus demonstrates each exercise, with tempo, sets, RPE and the cues that matter.'],
  ['Coach-built', 'Programming designed by a trainer who has coached thousands, not scraped from the internet.'],
  ['Track what matters', 'Weight, reps and RPE in seconds. PBs, key lifts and training coverage over time.'],
  ['A real person to ask', 'Direct chat with your coach, inside the app.'],
];

const COMMUNITY_TILES = [
  ['Feed', 'Real sessions'],
  ['Leaderboard', 'Monthly points'],
  ['Rewards', 'Prize draws'],
];

// Real App Store reviews, lightly tidied for spelling. Add more here as they come in.
const REVIEWS = [
  {
    title: 'Love it - like having a PT',
    quote:
      "It's like having a PT but for a fraction of the cost. Very easy to use and really nicely structured workouts and plans. Really rating the hybrid running and strength plan I'm on at the moment. I've been working out for 15 years planning myself and this has taken me to another level!",
    who: 'App Store review · Hybrid programme',
  },
  {
    title: 'Fantastic!!!',
    quote:
      'This app is great. From the design to the workouts. Really easy to use and understand. The workouts are great and varied. Has helped me immensely on my fitness journey.',
    who: 'App Store review',
  },
];

const PRICING_ROWS = [
  'Running, strength and hybrid programmes',
  'Unlimited generated workouts, six types',
  'Movement videos, cues, stats and PBs',
  'Community, leaderboards and coach chat',
];

const Stars = ({ className = '' }: { className?: string }) => (
  <span className={`bh-stars ${className}`.trim()} aria-label="Five stars" role="img">
    ★★★★★
  </span>
);

const AppleMark = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const Home = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  useRevealOnScroll();

  useEffect(() => {
    document.title = 'Burst | The PT in your pocket';
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const target = document.getElementById(id);
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  const handleNavClick = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <div className="bh">
      {/* Nav */}
      <header className="bh-nav">
        <div className="bh-inner bh-nav__inner">
          <a className="bh-nav__brand" href="/" aria-label="Burst home">
            <img src={wordmark} alt="Burst" width={733} height={253} />
          </a>

          <nav className="bh-nav__links" aria-label="Sections">
            {NAV_LINKS.map((link) => (
              <a key={link.id} href={`#${link.id}`} onClick={handleNavClick(link.id)}>
                {link.label}
              </a>
            ))}
          </nav>

          <div className="bh-nav__actions">
            <ArrowPill
              href={APP_STORE_URL}
              className="bh-nav__cta"
              onClick={() => trackStoreClick('ios', 'nav')}
            >
              <span className="bh-nav__cta-long">Download the app</span>
              <span className="bh-nav__cta-short">Download</span>
            </ArrowPill>
            <button
              type="button"
              className={`bh-nav__menu ${menuOpen ? 'is-open' : ''}`.trim()}
              aria-expanded={menuOpen}
              aria-controls="bh-mobile-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>

        <nav
          id="bh-mobile-menu"
          className="bh-mobile-menu"
          aria-label="Sections"
          hidden={!menuOpen}
        >
          {NAV_LINKS.map((link) => (
            <a key={link.id} href={`#${link.id}`} onClick={handleNavClick(link.id)}>
              {link.label}
            </a>
          ))}
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className="bh-hero" aria-labelledby="bh-hero-title">
          <div className="bh-inner bh-hero__inner">
            <p className="bh-eyebrow bh-eyebrow--ink"></p>
            <p className="bh-eyebrow bh-eyebrow--ink">Built on the methods of a real coach. Not AI.</p>
            <h1 id="bh-hero-title" className="bh-h1">
              The PT in your pocket.
              <span className="bh-h1__accent">Elite coaching that fits your life.</span>
            </h1>
            <p className="bh-hero__sub">
              Running, strength and hybrid programmes, plus single workouts generated from
              your time, kit and goal. For normal people with busy lives.
            </p>
            <StoreBadges location="hero" />
            <p className="bh-hero__rating">
              <Stars className="bh-stars--gold" /> 5.0 on the App Store · Free trial, then £9.99/month
            </p>

            <div className="bh-hero__phones">
              <PhoneFrame src={screenTrainingWeek} alt="Weekly training view" className="bh-hero__phone bh-hero__phone--side" eager />
              <PhoneFrame src={screenHomeDark} alt="Burst home screen" size="lg" className="bh-hero__phone bh-hero__phone--centre" eager />
              <PhoneFrame src={screenCommunityRun} alt="Community run post" className="bh-hero__phone bh-hero__phone--side bh-hero__phone--third" eager />
            </div>
          </div>
        </section>

        {/* Proof strip */}
        <section className="bh-section bh-section--cream bh-proof" aria-label="Why people choose Burst">
          <div className="bh-inner">
            <ul className="bh-proof__grid">
              <li className="bh-card bh-proof__card" data-reveal>
                <span className="bh-proof__title">Built by a real coach.<br />Not AI.</span>
                <span className="bh-proof__sub">Every session is Gus Barton's programming.</span>
              </li>
              <li className="bh-card bh-proof__card" data-reveal>
                <span className="bh-proof__title">Run and lift in one plan.<br /></span>
                <span className="bh-proof__sub">Hybrid programmes that balance both.</span>
              </li>
              <li className="bh-card bh-proof__card" data-reveal>
                <span className="bh-proof__title">Short on time?<br />We've got you.</span>
                <span className="bh-proof__sub">Workouts built to your time and kit.</span>
              </li>
              <li className="bh-card bh-proof__card" data-reveal>
                <span className="bh-proof__title">
                  <span className="bh-proof__num">5.0</span> on the<br />App Store
                </span>
                <span className="bh-proof__sub"><Stars /> Free trial included.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="bh-section bh-section--cream bh-how" aria-labelledby="bh-how-title">
          <div className="bh-inner">
            <div className="bh-section-head">
              <p className="bh-eyebrow">How it works</p>
              <h2 id="bh-how-title" className="bh-h2 bh-how__title">Tell us your time, kit and goal. We build the rest.</h2>
              <p className="bh-lead">A full programme, and every session in it, in three steps.</p>
            </div>
            <ol className="bh-how__grid">
              {STEPS.map((step) => (
                <li key={step.number} className="bh-card bh-card--lg bh-step" data-reveal>
                  <div className="bh-step__panel">
                    <PhoneFrame src={step.screen} alt={step.alt} size="sm" surface="cream" />
                  </div>
                  <div className="bh-step__row">
                    <span className="bh-step__num" aria-hidden="true">{step.number}</span>
                    <div>
                      <h3 className="bh-step__title">{step.title}</h3>
                      <p className="bh-step__body">{step.body}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Programmes and workouts (ink block) */}
        <div className="bh-ink-wrap">
          <div className="bh-ink-block">
            <section id="programmes" className="bh-programmes" aria-labelledby="bh-programmes-title">
              <div className="bh-inner">
                <div className="bh-section-head">
                  <p className="bh-eyebrow bh-eyebrow--ink">Programmes</p>
                  <h2 id="bh-programmes-title" className="bh-h2">Multi-week plans, built around a goal.</h2>
                  <p className="bh-lead bh-lead--ink bh-programmes__lead">
                    Choose one. Burst schedules every session across the weeks you've got, and moves days when life does.
                  </p>
                </div>
                <ul className="bh-programmes__grid">
                  {PROGRAMMES.map((programme) => (
                    <li key={programme.title} className="bh-programme" data-reveal>
                      <span className={`bh-programme__stripe bh-programme__stripe--${programme.stripe}`} aria-hidden="true" />
                      <div className="bh-programme__body">
                        <span className="bh-tile-label">{programme.label}</span>
                        <h3 className="bh-programme__title">{programme.title}</h3>
                        <p className="bh-programme__text">{programme.body}</p>
                        <dl className="bh-programme__facts">
                          {programme.facts.map(([label, value]) => (
                            <div key={label} className="bh-programme__fact">
                              <dt>{label}</dt>
                              <dd>{value}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section id="workouts" className="bh-workouts" aria-labelledby="bh-workouts-title">
              <div className="bh-inner bh-two-col">
                <div className="bh-two-col__copy bh-workouts__copy">
                  <p className="bh-eyebrow bh-eyebrow--ink">Workouts</p>
                  <h2 id="bh-workouts-title" className="bh-h2">Or just find something to do.</h2>
                  <p className="bh-lead bh-lead--ink">
                    No programme, no problem. Pick a type, tell us how long you've got and what kit is in front of
                    you. Burst generates a single session, sets and rest included.
                  </p>
                  <ul className="bh-types">
                    {WORKOUT_TYPES.map((type) => (
                      <li key={type.label} className="bh-tile bh-types__item">
                        <span className={`bh-types__dot bh-types__dot--${type.tone}`} aria-hidden="true" />
                        <span className="bh-types__label">{type.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bh-phone-pair" data-reveal>
                  <PhoneFrame src={screenWorkoutGrid} alt="Choosing a workout type" />
                  <PhoneFrame src={screenEquipmentFilter} alt="Equipment filter" className="bh-phone-pair__offset" />
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Why Burst */}
        <section id="coaching" className="bh-section bh-section--cream bh-why" aria-labelledby="bh-why-title">
          <div className="bh-inner bh-two-col bh-two-col--wide bh-two-col--phones-first">
            <div className="bh-phone-pair" data-reveal>
              <PhoneFrame src={screenMovementBench} alt="Movement instructions and cues" surface="cream" />
              <PhoneFrame src={screenHistory} alt="Lift history" surface="cream" className="bh-phone-pair__offset" />
            </div>
            <div className="bh-two-col__copy bh-why__copy">
              <p className="bh-eyebrow">Why Burst</p>
              <h2 id="bh-why-title" className="bh-h2">Built by a real coach. Not AI.</h2>
              <dl className="bh-why__list">
                {WHY_ROWS.map(([term, detail]) => (
                  <div key={term} className="bh-why__row">
                    <dt>{term}</dt>
                    <dd>{detail}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* Devices */}
        <section className="bh-section bh-section--cream bh-devices" aria-labelledby="bh-devices-title">
          <div className="bh-inner">
            <div className="bh-card bh-card--lg bh-devices__card" data-reveal>
              <div className="bh-devices__copy">
                <p className="bh-eyebrow">Works with your watch</p>
                <h2 id="bh-devices-title" className="bh-devices__title">
                  Runs go out to your watch. Results come back to your feed.
                </h2>
                <p className="bh-devices__text">
                  Every run session is sent from the app to your Garmin or Apple Watch with the intervals and
                  paces set. Finish it and it lands in Burst with pace, heart rate and route.
                </p>
              </div>
              <div className="bh-devices__badges">
                <span className="bh-device bh-device--garmin">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3 2 21h20L12 3z" /></svg>
                  GARMIN
                </span>
                <span className="bh-device bh-device--apple">
                  <AppleMark />
                  Watch
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Community (ink block) */}
        <div className="bh-ink-wrap">
          <section id="community" className="bh-ink-block bh-community" aria-labelledby="bh-community-title">
            <div className="bh-inner bh-two-col bh-two-col--phones-first">
              <div className="bh-phone-pair bh-phone-pair--offset-first" data-reveal>
                <PhoneFrame src={screenLeaderboard} alt="Monthly leaderboard" className="bh-phone-pair__offset" />
                <PhoneFrame src={screenCommunityMuscle} alt="Community feed with muscle map" />
              </div>
              <div className="bh-two-col__copy bh-community__copy">
                <p className="bh-eyebrow bh-eyebrow--ink">Community</p>
                <h2 id="bh-community-title" className="bh-h2">Train alone. Never train on your own.</h2>
                <p className="bh-lead bh-lead--ink">
                  Every workout earns points. Monthly leaderboards overall and by exercise, prize draws, and a feed
                  of real sessions: runs, lifts, routes and muscle maps.
                </p>
                <ul className="bh-community__tiles">
                  {COMMUNITY_TILES.map(([label, value]) => (
                    <li key={label} className="bh-tile">
                      <span className="bh-tile-label bh-tile-label--ink">{label}</span>
                      <span className="bh-tile__value">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>

        {/* The coach */}
        <section className="bh-section bh-section--cream bh-coach" aria-labelledby="bh-coach-title">
          <div className="bh-inner bh-coach__grid">
            <img
              className="bh-coach__photo"
              src={gusPhoto}
              alt="Gus Barton running"
              width={480}
              height={600}
              loading="lazy"
              decoding="async"
              data-reveal
            />
            <div className="bh-coach__copy">
              <p className="bh-eyebrow">The coach</p>
              <h2 id="bh-coach-title" className="bh-h2">Built on the methods of Gus Barton.</h2>
              <p className="bh-lead bh-coach__lead">
                Gus is a UK personal trainer and endurance athlete who has coached thousands of people to their
                goals, including 25 to world records. Burst is built on his methods: the programming, the cues and
                the progressions, made for normal schedules, not pro diaries.
              </p>
              <dl className="bh-stats">
                <div className="bh-stat">
                  <dt className="bh-tile-label">World records coached</dt>
                  <dd className="bh-stat__value">25</dd>
                </div>
                <div className="bh-stat">
                  <dt className="bh-tile-label">People coached</dt>
                  <dd className="bh-stat__value">1,000s</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="bh-section bh-section--cream bh-reviews" aria-labelledby="bh-reviews-title">
          <div className="bh-inner">
            <div className="bh-section-head bh-reviews__head">
              <Stars className="bh-stars--lg" />
              <h2 id="bh-reviews-title" className="bh-h2">Rated 5.0 on the App Store</h2>
            </div>
            <ul className={`bh-reviews__grid ${REVIEWS.length === 2 ? 'bh-reviews__grid--two' : ''}`.trim()}>
              {REVIEWS.map((review, index) => (
                <li key={index} className="bh-card bh-card--lg bh-review" data-reveal>
                  <h3 className="bh-review__title">{review.title}</h3>
                  <blockquote className="bh-review__quote">"{review.quote}"</blockquote>
                  <p className="bh-review__who">{review.who}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="bh-section bh-section--cream bh-pricing" aria-labelledby="bh-pricing-title">
          <div className="bh-inner bh-pricing__inner">
            <div className="bh-section-head bh-pricing__head">
              <p className="bh-eyebrow">Pricing</p>
              <h2 id="bh-pricing-title" className="bh-h2">Simple. One plan.</h2>
            </div>
            <div className="bh-card bh-card--lg bh-pricing__card" data-reveal>
              <p className="bh-pricing__price">
                <span className="bh-pricing__amount">£9.99</span>
                <span className="bh-pricing__period">/ month</span>
              </p>
              <ul className="bh-pricing__rows">
                {PRICING_ROWS.map((row) => (
                  <li key={row}>
                    <span>{row}</span>
                    <span className="bh-pricing__tick" aria-hidden="true">✓</span>
                  </li>
                ))}
              </ul>
              <ArrowPill
                href={APP_STORE_URL}
                large
                className="bh-pricing__cta"
                onClick={() => trackStoreClick('ios', 'pricing')}
              >
                Start your free trial
              </ArrowPill>
              <p className="bh-pricing__note">Cancel any time from your App Store settings.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="bh-footer">
        <div className="bh-inner bh-footer__inner">
          <span className="bh-footer__brand">Burst Athletics</span>
          <nav className="bh-footer__links" aria-label="Footer">
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
            {INSTAGRAM_URL && (
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a>
            )}
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Home;
